// ============================================================
// KillTeamsContext (Squad Mode, 2026-10-23; plan modes-rework) - the
// kill-teams read off the sheets, the GM's acting-leader answers and the
// Cohesion pools, for the order strip's meter, the actor strip and the lanes.
// Composed in the shell (useShellContexts) from useKillTeams and the
// verbs of useKillTeamActions.
// ============================================================
import { createContext, useContext, type ReactNode } from 'react';
import type { KillTeamsState } from '../hooks/useKillTeams';
import type useKillTeamActions from '../hooks/useKillTeamActions';
import type { Brother } from '../engine/plate/bscope';
import type { SupportView } from '../engine/killTeam/support';
import type { Combatant } from '../engine/roster/types';

export type KillTeamActions = ReturnType<typeof useKillTeamActions>;

/** The documents, and the verbs where the table has them (absent in a test that needs only the reading). */
export type KillTeamsValue = KillTeamsState & {
  actions?: KillTeamActions;
  /** His brothers around him on the map, for his plate's B-scope (read when asked: a marker's move re-renders nothing). */
  around?: (id: string) => Brother[];
  /**
   * His support ring and threads (Squad Mode), over the rule markers; null when there is nothing to draw. `preview`:
   * he is aiming at Enter or Challenge, so a Solo brother sees what he would have (hooks/useSquadPreview).
   */
  support?: (actorId: string | null, roster: readonly Combatant[], preview?: boolean) => SupportView | null;
};

const KillTeamsContext = createContext<KillTeamsValue | null>(null);

export function KillTeamsProvider({ value, children }: { value: KillTeamsValue | null; children: ReactNode }) {
  return <KillTeamsContext.Provider value={value}>{children}</KillTeamsContext.Provider>;
}

/** The kill-teams, or null outside the table (the Assets page, a test without the provider). */
export function useKillTeamsContext(): KillTeamsValue | null {
  return useContext(KillTeamsContext);
}
